import { Flight, Hotel, ItineraryItem } from '../../types/Quotation';
import { parseDateValue } from '../../utils/date';

export type InclusionItem =
  | {
    key: string;
    type: 'flight';
    flight: Flight & { day: number };
  }
  | {
    key: string;
    type: 'hotel';
    hotel: Hotel & { day: number };
  }
  | {
    key: string;
    type: 'transfer';
    transfer: {
      cabType?: string;
      maxOccupancy?: number;
      transferType?: string;
    };
  };

export type InclusionPaginationOptions = {
  availableHeight?: number;
  titleHeight?: number;
  categoryHeadingHeight?: number;
  itemGap?: number;
  maxItemsPerPage?: number;
  itemHeights?: Record<string, number>;
  defaultItemHeights?: Record<InclusionItem['type'], number>;
};

export const DEFAULT_INCLUSION_ITEM_HEIGHTS: Record<InclusionItem['type'], number> = {
  flight: 200,
  hotel: 220,
  transfer: 180,
};

export const DEFAULT_INCLUSION_PAGINATION: Required<Omit<InclusionPaginationOptions, 'itemHeights' | 'defaultItemHeights'>> & {
  defaultItemHeights: Record<InclusionItem['type'], number>;
} = {
  availableHeight: 860,
  titleHeight: 40,
  categoryHeadingHeight: 44,
  itemGap: 16,
  maxItemsPerPage: 3,
  defaultItemHeights: DEFAULT_INCLUSION_ITEM_HEIGHTS,
};

const resolveHotelKey = (hotel: Partial<Hotel>) => {
  const anyHotel = hotel as any;
  return String(
    anyHotel.hotelId ||
    anyHotel.hotel_id ||
    anyHotel.hotelRef ||
    anyHotel.hotel_ref ||
    anyHotel.providerHotelId ||
    anyHotel.provider_hotel_id ||
    anyHotel.id ||
    hotel.name ||
    ''
  )
    .trim()
    .toLowerCase();
};

const resolveHotelStayKey = (hotel: Partial<Hotel>) => {
  const anyHotel = hotel as any;
  return [
    resolveHotelKey(hotel),
    String(anyHotel.checkInDate || anyHotel.check_in_date || '').trim(),
    String(anyHotel.checkOutDate || anyHotel.check_out_date || '').trim(),
  ].join('|');
};

const resolveHotelCheckInTime = (hotel: Partial<Hotel>) => {
  const anyHotel = hotel as any;
  const parsed = parseDateValue(anyHotel.checkInDate || anyHotel.check_in_date || '');
  return parsed && !Number.isNaN(parsed.getTime()) ? parsed.getTime() : Number.POSITIVE_INFINITY;
};

const PAGE_FIT_SLACK = 80;

export const buildOrderedInclusionItems = (itinerary: ItineraryItem[]): InclusionItem[] => {
  const flights = itinerary.flatMap((day, dayIndex) =>
    (day.flights || []).map((flight, flightIndex) => ({
      key: `flight-${day.day}-${dayIndex}-${flight.provider || flight.airline || 'airline'}-${flight.flightNumber || flightIndex}-${flightIndex}`,
      type: 'flight' as const,
      flight: { ...flight, day: day.day },
    }))
  );

  const seenHotels = new Set<string>();
  const hotels: Array<InclusionItem & { sortIndex: number; checkInTime: number }> = [];
  let hotelSortIndex = 0;

  itinerary.forEach((day, dayIndex) => {
    (day.hotels || []).forEach((hotel, hotelIndex) => {
      const hotelKey = resolveHotelStayKey(hotel);
      if (!hotelKey || seenHotels.has(hotelKey)) return;
      seenHotels.add(hotelKey);
      hotels.push({
        key: `hotel-${day.day}-${dayIndex}-${hotelKey || hotelIndex}-${hotelIndex}`,
        type: 'hotel',
        hotel: { ...hotel, day: day.day },
        sortIndex: hotelSortIndex,
        checkInTime: resolveHotelCheckInTime(hotel),
      });
      hotelSortIndex += 1;
    });
  });

  hotels.sort((left, right) => {
    if (left.checkInTime !== right.checkInTime) return left.checkInTime - right.checkInTime;
    return left.sortIndex - right.sortIndex;
  });

  return [...flights, ...hotels.map(({ sortIndex, checkInTime, ...item }) => item)];
};

export const paginateInclusionItems = (
  items: InclusionItem[],
  options: InclusionPaginationOptions = {}
): InclusionItem[][] => {
  const {
    availableHeight,
    titleHeight,
    categoryHeadingHeight,
    itemGap,
    maxItemsPerPage,
    itemHeights = {},
    defaultItemHeights,
  } = {
    ...DEFAULT_INCLUSION_PAGINATION,
    ...options,
    defaultItemHeights: {
      ...DEFAULT_INCLUSION_PAGINATION.defaultItemHeights,
      ...(options.defaultItemHeights || {}),
    },
  };

  if (items.length === 0) return [];

  const pages: InclusionItem[][] = [];
  let index = 0;

  while (index < items.length) {
    const page: InclusionItem[] = [];
    const seenTypes = new Set<InclusionItem['type']>();
    let usedHeight = titleHeight;

    while (index < items.length && page.length < maxItemsPerPage) {
      const item = items[index];
      const itemHeight = itemHeights[item.key] || defaultItemHeights[item.type];
      const needsCategoryHeading = !seenTypes.has(item.type);
      const nextHeight =
        (page.length > 0 ? itemGap : 0) +
        (needsCategoryHeading ? categoryHeadingHeight : 0) +
        itemHeight;

      if (page.length > 0 && usedHeight + nextHeight > availableHeight + PAGE_FIT_SLACK) {
        break;
      }

      page.push(item);
      seenTypes.add(item.type);
      usedHeight += nextHeight;
      index += 1;

      if (page.length >= maxItemsPerPage) {
        break;
      }
    }

    if (page.length === 0) {
      page.push(items[index]);
      index += 1;
    }

    pages.push(page);
  }

  return pages;
};
